import { AdminPermissions } from "@/enums";
import { AdminData, Settings } from "@/interfaces";
import {
  isNonEmptyArray,
  isNonEmptyObject,
  isNonEmptyString,
  isNumber,
  isObject,
  stringToIntArray,
} from "@/utils";

export class SettingsHelper {
  static toDto = (settings?: Settings, keepAdmins: boolean = false) => ({
    ...settings,
    productBarCodeLength: (
      settings?.productBarCodeLength as number[]
    )?.join?.(),
    creditCardNumberLength: (
      settings?.creditCardNumberLength as number[]
    )?.join?.(),
    dealerBadgeCodeLength: (
      settings?.dealerBadgeCodeLength as number[]
    )?.join?.(),
    ...(!keepAdmins &&
      isNonEmptyArray(settings?.admins) && {
        admins: JSON.stringify(settings?.admins),
      }),
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static fromDto = (dto?: any, keepAdmins: boolean = false) => {
    let admins = keepAdmins ? dto?.admins : undefined;

    if (!keepAdmins && isNonEmptyString(dto?.admins)) {
      try {
        admins = JSON.parse(dto?.admins);
        if (!Array.isArray(admins) && isObject(admins)) {
          admins = [admins];
        }
      } catch (error) {
        console.log('"%o" parse "%o"', dto?.admins, error);
      }
    }

    return {
      settingId: dto?.settingId,
      adminPassword: dto?.adminPassword,
      shippingFee: dto?.shippingFee,
      scannerInputDebounceWait: dto?.scannerInputDebounceWait,
      creditCardNumberLength: stringToIntArray(dto?.creditCardNumberLength),
      productBarCodeLength: stringToIntArray(dto?.productBarCodeLength),
      dealerBadgeCodeLength: stringToIntArray(dto?.dealerBadgeCodeLength),
      defaultProductImageUrl: dto?.defaultProductImageUrl,
      fedExEmail: dto?.fedExEmail,
      claimFee: dto?.claimFee,
      coopEligible: dto?.coopEligible,
      couponAmount: dto?.couponAmount,
      orderEmailBcc: dto?.orderEmailBcc,
      packingSlipEmail: dto?.packingSlipEmail,
      ...(isNonEmptyArray(admins) && { admins }),
    };
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static toAdminData = (data?: any) => {
    if (!data) return undefined;

    if (isNonEmptyObject(data)) data = [data];

    if (!isNonEmptyArray(data)) return undefined;

    const result = [] as AdminData[];
    for (let i = 0, l = data.length; i < l; ++i) {
      const _data = data[i];
      const admin = isNonEmptyObject(_data)
        ? {
            ...(isNonEmptyString(_data?.email) && {
              email: _data?.email?.trim?.().toLowerCase(),
            }),
            ...(isNonEmptyString(_data?.password) && {
              password: _data?.password,
            }),
            ...(isNumber(_data.permissions) && {
              permissions: _data.permissions,
            }),
          }
        : undefined;

      if (isNonEmptyObject(admin)) result.push(admin as AdminData);
    }

    return isNonEmptyArray(result) ? result : undefined;
  };

  static setPermission = (perms?: AdminPermissions, perm?: AdminPermissions) =>
    (perms as number) | (perm as number);

  static unsetPermission = (
    perms?: AdminPermissions,
    perm?: AdminPermissions
  ) => (perms as number) & ~(perm as number);

  static hasPermission = (perms?: AdminPermissions, check?: AdminPermissions) =>
    ((perms as number) & (check as number)) === check;

  static hasProductsPermission = (perms?: AdminPermissions) =>
    SettingsHelper.hasPermission(perms, AdminPermissions.PRODUCTS);

  static hasOrdersPermission = (perms?: AdminPermissions) =>
    SettingsHelper.hasPermission(perms, AdminPermissions.ORDERS);

  static hasShippingPortalPermission = (perms?: AdminPermissions) =>
    SettingsHelper.hasPermission(perms, AdminPermissions.SHIPPING_PORTAL);

  static hasAttendeesPermission = (perms?: AdminPermissions) =>
    SettingsHelper.hasPermission(perms, AdminPermissions.ATTENDEES);

  static hasReportingPermission = (perms?: AdminPermissions) =>
    SettingsHelper.hasPermission(perms, AdminPermissions.REPORTING);

  static hasInventoryCountsPermission = (perms?: AdminPermissions) =>
    SettingsHelper.hasPermission(perms, AdminPermissions.INVENTORY_COUNTS);

  static hasSettingsPermission = (perms?: AdminPermissions) =>
    SettingsHelper.hasPermission(perms, AdminPermissions.SETTINGS);

  static setPermissions = (
    hasProductsPermission?: boolean,
    hasOrdersPermission?: boolean,
    hasShippingPortalPermission?: boolean,
    hasAttendeesPermission?: boolean,
    hasReportingPermission?: boolean,
    hasInventoryCountsPermission?: boolean,
    hasSettingsPermission?: boolean
  ) =>
    (hasProductsPermission
      ? SettingsHelper.setPermission(
          AdminPermissions.NONE,
          AdminPermissions.PRODUCTS
        )
      : AdminPermissions.NONE) |
    (hasOrdersPermission
      ? SettingsHelper.setPermission(
          AdminPermissions.NONE,
          AdminPermissions.ORDERS
        )
      : AdminPermissions.NONE) |
    (hasShippingPortalPermission
      ? SettingsHelper.setPermission(
          AdminPermissions.NONE,
          AdminPermissions.SHIPPING_PORTAL
        )
      : AdminPermissions.NONE) |
    (hasAttendeesPermission
      ? SettingsHelper.setPermission(
          AdminPermissions.NONE,
          AdminPermissions.ATTENDEES
        )
      : AdminPermissions.NONE) |
    (hasReportingPermission
      ? SettingsHelper.setPermission(
          AdminPermissions.NONE,
          AdminPermissions.REPORTING
        )
      : AdminPermissions.NONE) |
    (hasInventoryCountsPermission
      ? SettingsHelper.setPermission(
          AdminPermissions.NONE,
          AdminPermissions.INVENTORY_COUNTS
        )
      : AdminPermissions.NONE) |
    (hasSettingsPermission
      ? SettingsHelper.setPermission(
          AdminPermissions.NONE,
          AdminPermissions.SETTINGS
        )
      : AdminPermissions.NONE);
}
